import React, { useMemo } from 'react'
import { Question } from '@/features/survey/survey-types'
import { FormLayout } from '@/components/template/form-layout'
import { SurveyScoreForm } from '@/components/organisms/survey/survey-score-form'
import { SurveyNpsForm } from '@/components/organisms/survey/survey-nps-form'
import { SurveyChoiceSingleForm } from '@/components/organisms/survey/survey-choice-single-form'
import { SurveyChoiceMultipleForm } from '@/components/organisms/survey/survey-choice-multiple-form'
import { SurveyTextForm } from '@/components/organisms/survey/survey-text-form'
import { Button } from '@/components/atoms/button'
import { formButtonStyle } from '@/styles/form'
import { SurveyTitle } from '@/components/atoms/survey-title'
import { usePickByLanguage } from '@/hooks/use-pick-by-language'
import { Divider } from '@/components/atoms/divider'
import { css } from '@emotion/react'
import { useFormContext, useWatch } from 'react-hook-form'
import { SurveyFormData } from '@/features/survey/survey-types'

type Props = {
  questions: Question[]
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void
  buttonText: string
}

// 質問タイプとコンポーネントのマッピング
const questionTypeComponentMap = {
  score: SurveyScoreForm,
  nps: SurveyNpsForm,
  choice_single: SurveyChoiceSingleForm,
  choice_multiple: SurveyChoiceMultipleForm,
  text: SurveyTextForm,
} as const

/**
 * アンケートフォームコンポーネント
 * 質問タイプに応じて適切なフォームコンポーネントを表示
 */
export const SurveyForm = ({ questions, onSubmit, buttonText }: Props) => {
  const pickByLanguage = usePickByLanguage()
  const { control } = useFormContext()

  // フォームの値を監視（値が変更されるたびに再レンダリングされる）
  const answers: SurveyFormData = useWatch({ control, name: 'answers' }) || []

  // 必須項目がすべて入力されているかをチェック
  const isAllRequiredFieldsFilled = useMemo(() => {
    return questions.every((question, index) => {
      // 必須項目でない場合はスキップ
      if (!question.isRequired) {
        return true
      }

      const answer = answers[index]
      if (!answer) {
        return false
      }

      // 質問タイプに応じて値をチェック
      switch (question.type) {
        case 'score':
        case 'nps':
          return answer.answerNumber !== undefined && answer.answerNumber !== null
        case 'choice_single':
        case 'text':
          return answer.answerText !== undefined && String(answer.answerText).trim() !== ''
        case 'choice_multiple':
          return Array.isArray(answer.answerChoices) && answer.answerChoices.length > 0
        default:
          return true
      }
    })
  }, [questions, answers])

  return (
    <form onSubmit={onSubmit}>
      {questions.map((question, index) => (
        <React.Fragment key={question.id}>
          {/* 最初の質問以外は区切り線を表示 */}
          {index !== 0 && <Divider />}
          <FormLayout>
            <SurveyTitle title={pickByLanguage(question.questionText)} isRequired={question.isRequired} />
            {/* 質問タイプに応じてフォームコンポーネントを切り替え */}
            {(() => {
              const QuestionFormComponent = questionTypeComponentMap[question.type]
              return QuestionFormComponent && <QuestionFormComponent question={question} index={index} />
            })()}
          </FormLayout>
        </React.Fragment>
      ))}
      <Button
        buttonCss={[formButtonStyle, css({ marginTop: '2rem' })]}
        text={buttonText}
        type="submit"
        disabled={!isAllRequiredFieldsFilled}
      />
    </form>
  )
}
